import { Fragment, type ReactNode } from 'react';
import { useMemo, useState } from 'react';
import {
  Cell,
  Column,
  ResizableTableContainer,
  Row,
  Separator,
  Table,
  TableBody,
  TableHeader,
  type ColumnProps,
  type Key,
  type SortDescriptor,
} from 'react-aria-components';
import { Flex, Text } from '@backstage/ui';
import { RiMedalLine } from '@remixicon/react';
import { useLevels } from '../../hooks/useLevels';
import styles from './LeveledTable.module.css';

export interface ColumnDef<T> {
  id: string;
  /** Header label. Empty string renders a visually hidden header. */
  label: string;
  /** Column width — same type as React Aria's Column width prop. e.g. '1fr', 90, '50%'. */
  width?: ColumnProps['width'];
  minWidth?: ColumnProps['minWidth'];
  maxWidth?: ColumnProps['maxWidth'];
  allowsSorting?: boolean;
  isRowHeader?: boolean;
  renderCell: (item: T) => ReactNode;
}

export interface LeveledTableProps<T> {
  items: T[];
  getLevelId: (item: T) => string | null;
  getItemKey: (item: T) => Key;
  columns: ColumnDef<T>[];
  toolbar?: ReactNode;
  emptyState?: ReactNode;
  defaultSort?: SortDescriptor;
  /** Sort comparator. Return negative/zero/positive; direction is applied by LeveledTable. */
  compare?: (a: T, b: T, column: string) => number;
  // Expand support
  expandedKey?: Key | null;
  onExpandChange?: (key: Key | null) => void;
  renderExpandedRow?: (item: T) => ReactNode;
}

// Internal flat-list entry: one per row plus one per open expanded panel.
type Entry<T> =
  | { kind: 'row'; item: T; id: Key }
  | { kind: 'expanded'; item: T; id: Key };

interface GroupProps<T> {
  label: string;
  levelId: string | null;
  items: T[];
  columns: ColumnDef<T>[];
  getItemKey: (item: T) => Key;
  sort: SortDescriptor | undefined;
  onSortChange: (d: SortDescriptor) => void;
  expandedKey?: Key | null;
  renderExpandedRow?: (item: T) => ReactNode;
}

function LevelGroup<T>({
  label,
  levelId,
  items,
  columns,
  getItemKey,
  sort,
  onSortChange,
  expandedKey,
  renderExpandedRow,
}: GroupProps<T>) {
  const entries = useMemo<Entry<T>[]>(() => {
    const result: Entry<T>[] = [];
    for (const item of items) {
      const id = getItemKey(item);
      result.push({ kind: 'row', item, id });
      if (renderExpandedRow && expandedKey === id) {
        result.push({ kind: 'expanded', item, id: `${id}--x` });
      }
    }
    return result;
  }, [items, getItemKey, renderExpandedRow, expandedKey]);

  const colSpan = columns.length;
  const colorVar =
    levelId !== null
      ? `var(--zf-sc-level-${levelId})`
      : 'var(--zf-sc-level-others)';

  if (!entries.length) return null;

  return (
    <Flex direction="column" gap="2">
      <Flex align="center" gap="2">
        <RiMedalLine size={28} style={{ color: colorVar, flexShrink: 0 }} />
        <Text as="h4" variant="title-x-small" weight="bold" color="secondary">
          {label}
        </Text>
      </Flex>
      <ResizableTableContainer className={styles.container}>
        <Table
          aria-label={`${label} checks`}
          sortDescriptor={sort}
          onSortChange={onSortChange}
          className={styles.table}
        >
          <TableHeader columns={columns}>
            {col => (
              <Column
                id={col.id}
                isRowHeader={col.isRowHeader}
                allowsSorting={col.allowsSorting}
                width={col.width}
                minWidth={col.minWidth}
                maxWidth={col.maxWidth}
                className={styles.column}
              >
                <span>{col.label || <>&nbsp;</>}</span>
              </Column>
            )}
          </TableHeader>
          <TableBody items={entries} dependencies={[columns, expandedKey]}>
            {entry =>
              entry.kind === 'row' ? (
                <Row id={entry.id} columns={columns} className={styles.row}>
                  {col => (
                    <Cell className={styles.cell}>
                      {col.renderCell(entry.item)}
                    </Cell>
                  )}
                </Row>
              ) : (
                <Row id={entry.id} className={styles.expandedRow}>
                  <Cell
                    colSpan={colSpan}
                    className={styles.expandedCell}
                    focusMode="child"
                  >
                    {renderExpandedRow!(entry.item)}
                  </Cell>
                </Row>
              )
            }
          </TableBody>
        </Table>
      </ResizableTableContainer>
    </Flex>
  );
}

export function LeveledTable<T>({
  items,
  getLevelId,
  getItemKey,
  columns,
  toolbar,
  emptyState,
  defaultSort,
  compare,
  expandedKey,
  onExpandChange: _onExpandChange,
  renderExpandedRow,
}: LeveledTableProps<T>) {
  const { levels } = useLevels();
  const [sort, setSort] = useState<SortDescriptor | undefined>(defaultSort);

  const orderedLevels = useMemo(
    () => [...levels].sort((a, b) => a.id.localeCompare(b.id)),
    [levels],
  );

  const sortedItems = useMemo(() => {
    if (!sort || !compare) return items;
    return [...items].sort((a, b) => {
      const cmp = compare(a, b, sort.column as string);
      return sort.direction === 'descending' ? -cmp : cmp;
    });
  }, [items, sort, compare]);

  const grouped = useMemo(() => {
    const knownLevelIds = new Set(orderedLevels.map(l => l.id));
    const map = new Map<string | null, T[]>();
    for (const item of sortedItems) {
      const raw = getLevelId(item);
      const key = raw !== null && knownLevelIds.has(raw) ? raw : null;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(item);
    }
    return map;
  }, [sortedItems, getLevelId, orderedLevels]);

  const groupProps = {
    columns,
    getItemKey,
    sort,
    onSortChange: setSort,
    expandedKey,
    renderExpandedRow,
  };

  if (items.length === 0) {
    return (
      <>
        {toolbar && <div className={styles.toolbar}>{toolbar}</div>}
        {emptyState ?? null}
      </>
    );
  }

  const groups = [
    ...orderedLevels.map(lv => ({
      key: lv.id,
      label: lv.name,
      levelId: lv.id as string | null,
      items: grouped.get(lv.id) ?? [],
    })),
    {
      key: '__others',
      label: 'Others',
      levelId: null,
      items: grouped.get(null) ?? [],
    },
  ].filter(g => g.items.length > 0);

  return (
    <section>
      {toolbar && <div className={styles.toolbar}>{toolbar}</div>}
      <Flex direction="column" gap="6">
        {groups.map((g, i) => (
          <Fragment key={g.key}>
            {i > 0 && <Separator className={styles.separator} />}
            <LevelGroup
              label={g.label}
              levelId={g.levelId}
              items={g.items}
              {...groupProps}
            />
          </Fragment>
        ))}
      </Flex>
    </section>
  );
}
